import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import InputField from "~components/Form/components/InputField";
import { EditFormIcon, ModalClosed, TrashCanProp } from "~misc/icons";
import Select from "~components/Form/components/Select";
import * as yup from "yup";
import { contatoValidation } from "~services/utils/defaultValidations";
import mask from "~services/utils/mask";
import MaskedInput from "react-text-mask";
import useModalAction from "./hooks/useModalAction";
import ExcluirModal from "~pages/PricesTable/components/Modal/ExcluirModal";
import { useToast } from "@chakra-ui/react";
import Loader from "~components/Form/components/Loader";
import useOnlineStatus from "~hooks/useOnlineStatus";

const contatoModalValidation = yup.object().shape({
  ...contatoValidation(),
  cargoId: yup.number().nullable().required("O cargo é obrigatório!"),
  celular: yup
    .string()
    .test("contatos.celular", "Celular incompleto!", function (value) {
      if (!value) return true;

      return contatoValidation().celular.isValidSync(value);
    }),
});

const validation = yup.object().shape({
  contatos: yup.array().of(contatoModalValidation),
});

const buildTouchedContatos = (contatos = []) => ({
  contatos: contatos.map(() => ({
    nome: true,
    sobrenome: true,
    cargoId: true,
    email: true,
    telefone: true,
    celular: true,
  })),
});

const ModalContatoSalvos = ({ isOpen, onClose, initialValues, optionsCargo, setinitialValues, setFieldValue, deleteContato, formModalValues, setFormModalValues, formModalValuesCreate, setFormModalValuesCreate, onSaveContatos }) => {
  const isOnline = useOnlineStatus();
  const [contatosInit, setContatosInit] = useState([]);
  const modalClasses = isOpen ? "fixed inset-0 bg-black bg-opacity-0 z-[99999]" : "hidden";
  const overlayClasses = isOpen ? "fixed inset-0 bg-black opacity-50 z-40" : "hidden";
  const [contatoToDelete, setContatoToDelete] = React.useState(null);
  const { isModalOpenAction, closeModalAction, openModalAction } = useModalAction();
  const { formatNumber } = mask();


  const normalizeString = (str) => {
    if (str === null || str === undefined) return str;
    return String(str).trim().toLowerCase();
  };


  const isVend = (fonte) => {
    const normalized = normalizeString(fonte);
    return normalized === null || normalized === undefined || normalized === "vend+";
  };

  const toast = useToast();



  useEffect(() => {
    if (!initialValues?.contatos || initialValues.contatos.length <= 1) return;

    setFormModalValues(prev => {
      const userCreatedContatos = (prev.contatos || []).filter(c => c.idRandom);
      const initialContatos = initialValues.contatos.slice(1);
      const existingIds = new Set(initialContatos.map(c => c.idRandom || c.id));
      const uniqueUserCreated = userCreatedContatos.filter(c => !existingIds.has(c.idRandom || c.id));
      return {
        ...prev,
        contatos: [...initialContatos, ...uniqueUserCreated]
      };
    });
  }, [initialValues, setFormModalValues]);

  useEffect(() => {
    setFormModalValues(prev => {
      const existingContatos = prev.contatos || [];
      const newContatos = formModalValuesCreate.contatos || [];
      const existingIds = new Set(existingContatos.map(c => c.idRandom || c.id));
      const contatosToAdd = newContatos.filter(c => !existingIds.has(c.idRandom || c.id));
      return {
        ...prev,
        contatos: [...existingContatos, ...contatosToAdd]
      };
    });
  }, [formModalValuesCreate, setFormModalValues]);

  useEffect(() => {
    if (isOpen) {
      const contatosRestantes = formModalValues.contatos || [];
      setContatosInit(contatosRestantes);
    }
  }, [formModalValues.contatos, isOpen]);

  if (!isOpen) return null;

  const formikKey = (contatosInit || [])
    .map((contato) => contato.idRandom || contato.id || "novo")
    .join("-");

  const removeContato = (contatoId) => {
    const newContatosInit = contatosInit.filter(c => (c.idRandom || c.id) !== contatoId);
    setContatosInit(newContatosInit);

    setFormModalValues(prev => ({
      ...prev,
      contatos: prev?.contatos?.filter(c => (c.idRandom || c.id) !== contatoId) || [],
    }));

    setFormModalValuesCreate(prev => ({
      ...prev,
      contatos: prev?.contatos?.filter(c => (c.idRandom || c.id) !== contatoId) || [],
    }));

    if (newContatosInit.length < 1) {
      onClose();
    }
  };

  return (
    <>
      <div className={modalClasses}>
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-[1000px] px-2 py-14 mx-4 sm:mx-6 md:mx-6 rounded-[20px] shadow-lg z-50 relative">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="text-gray-500 p-4 rounded-tr-[20px] hover:text-gray-700 bg-quinaria absolute top-0 right-0"
              >
                <ModalClosed />
              </button>
            </div>
            <Formik
              key={formikKey}
              enableReinitialize
              initialValues={{ contatos: contatosInit }}
              validationSchema={validation}
              onSubmit={async (values) => {
                setFormModalValues({ contatos: values.contatos });

                if (onSaveContatos) {
                  const saved = await onSaveContatos(values.contatos);

                  if (saved === false) {
                    return;
                  }
                } else {
                  toast({
                    title: "Contato atualizado!",
                    description: "Para manter as alterações, não esqueça de enviar o formulário.",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                    position: "top-right",
                  });
                }

                onClose();
              }}
            >
              {({ values, setFieldValue, setFieldTouched, setFieldError, errors, touched, setTouched, submitForm, isSubmitting }) => (
                <Form>
                  <div className="overflow-y-auto max-h-[70vh] bg-white p-2 space-y-2">
                    {values.contatos.map((c, index) => {
                      const fonteNormalized = normalizeString(c.fonte);
                      const displayFonte = c.fonte == null || c.fonte == undefined ? "VenD+" : c.fonte;

                      return (
                        <div
                          key={c.idRandom || c.id || index}
                          className={`${
                            isVend(c.fonte)
                              ? "p-2 rounded-md bg-[#E2F0FF]"
                              : "p-2 rounded-md bg-[#FCF1F1]"
                          }`}
                        >
                          <div className="flex justify-between items-center border-b border-[#e2e8f0] pb-2">
                            <p className="font-bold max-w-[220px]">
                              {c.nome} {c.sobrenome}
                            </p>
                            <div className="flex justify-center gap-1 mt-1">

                                <button
                                  type="button"
                                  onClick={() => {
                                    setContatoToDelete(c.idRandom || c.id);
                                    openModalAction();
                                  }}
                                  className="w-8 h-8 bg-[#FF2E00] rounded-full flex items-center justify-center"
                                >
                                  <TrashCanProp className="icon" fill="#FFF" size={18} />
                                </button>

                            </div>
                            <p className="font-bold">{displayFonte}</p>
                          </div>

                          <div className="grid grid-cols-3 gap-4 ">
                            <InputField
                              labelName="Nome"
                              inputName={`contatos[${index}].nome`}
                              inputMask="offmask"
                              customClasses={"!bg-[#F7FAFC]"}
                              inputType="text"
                              value={values.contatos?.[index]?.nome || ""}
                            />
                            <InputField
                              labelName="Sobrenome"
                              inputName={`contatos[${index}].sobrenome`}
                              inputMask="offmask"
                              customClasses={"!bg-[#F7FAFC]"}
                              inputType="text"
                              value={values.contatos?.[index]?.sobrenome || ""}
                            />

                            <Select
                              name={`contatos[${index}].cargoId`}
                              label="Cargo"
                              options={optionsCargo}
                              value={optionsCargo.find((opt) => {
                                const contato = values.contatos?.[index];
                                if (!contato) return false;
                                if (contato.cargoId != null && String(opt.value) === String(contato.cargoId)) return true;
                                if (contato.cargo && opt.label === contato.cargo) return true;
                                return false;
                              }) || null}
                              onChange={(opt) => {
                                setFieldValue(`contatos[${index}].cargoId`, opt?.value ?? null);
                                setFieldValue(`contatos[${index}].cargo`, opt?.label ?? "", false);
                                setTimeout(() => setFieldTouched(`contatos[${index}].cargoId`, true, true));
                              }}
                              errors={errors.contatos?.[index]?.cargoId}
                              touched={touched.contatos?.[index]?.cargoId}
                            />

                            <InputField
                              labelName="Email"
                              inputName={`contatos[${index}].email`}
                              inputMask="offmask"
                              customClasses={"!bg-[#F7FAFC]"}
                              inputType="text"
                              value={values.contatos?.[index]?.email || ""}
                            />

                            <InputField
                              labelName="Telefone"
                              inputName={`contatos[${index}].telefone`}
                              as={MaskedInput}
                              mask={formatNumber}
                              customClasses={"!bg-[#F7FAFC]"}
                              inputMask="onmask"
                              inputType="text"
                              value={values.contatos?.[index]?.telefone || ""}
                            />

                            <InputField
                              labelName="Celular"
                              inputName={`contatos[${index}].celular`}
                              as={MaskedInput}
                              mask={formatNumber}
                              customClasses={"!bg-[#F7FAFC]"}
                              inputMask="onmask"
                              inputType="text"
                              value={values.contatos?.[index]?.celular || ""}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        className="mt-4 py-2 px-3 bg-[#EA3901] text-white opacity-100 rounded-lg font-bold text-[13px] button-search"
                        onClick={onClose}
                        disabled={isSubmitting}
                      >
                        Cancelar
                      </button>

                      {isOnline ? (
                        <button
                          type="button"
                          className="mt-4 py-2 px-3 bg-secundaria text-white opacity-100 rounded-lg font-bold text-[13px] button-search min-w-[96px] flex items-center justify-center gap-2 disabled:opacity-70"
                          disabled={isSubmitting}
                          onClick={async () => {
                            if (isSubmitting) {
                              return;
                            }

                            await setTouched(buildTouchedContatos(values.contatos), true);
                            await submitForm();
                          }}
                        >
                          {isSubmitting ? <Loader /> : "Salvar"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
          <div className={overlayClasses} onClick={onClose}></div>
        </div>
      </div>

      <ExcluirModal
        isOpen={isModalOpenAction}
        onClose={closeModalAction}
        onConfirm={() => {
          removeContato(contatoToDelete);
          closeModalAction();
        }}
        onCancel={closeModalAction}
        mensagem={`Tem certeza de que deseja excluir esse contato?`}
      />
    </>
  );
};

export default ModalContatoSalvos;
